/**
 * Series finalizadas: el estante de lo que ya no estas leyendo.
 *
 * Una serie que terminaste no deberia seguir ocupando sitio en la biblioteca
 * —ahi esta lo que estas leyendo— pero tampoco se puede tirar: quitarla de la
 * biblioteca de Suwayomi se lleva por delante el progreso y la deja fuera de
 * las copias, que es justo lo que le paso a One Piece. Asi que se queda dentro
 * y se marca.
 *
 * **La marca vive en el servidor**, en los metadatos de la serie
 * (`setMangaMeta`), y no en el aparato. Dos razones, las dos comprobadas y no
 * supuestas:
 *
 * 1. Lo que marcas en el iPhone se ve en el PC, como pasa con la mejora de
 *    imagen. En localStorage serian dos estantes distintos que no se hablan.
 * 2. **Los metadatos entran en el .tachibk**: se exporto una copia con una
 *    clave de prueba y la cadena aparece dentro del fichero. O sea que esto lo
 *    respalda solo la copia de las 4:30, sin tocar nada del servidor.
 *
 * Suwayomi solo guarda texto por clave, asi que todo va como JSON en una sola
 * clave. Lo que no se pueda leer se ignora: es preferible que la serie vuelva
 * a verse en la biblioteca a que la app se caiga por un JSON roto.
 */

import type { Capitulo, EntradaMeta } from '../api/consultas'
import { aMilisegundos } from './fechas'

export const CLAVE_META = 'yomou.finalizada'

/** Terminada de verdad o dejada a medias. Las dos salen de la biblioteca. */
export type Veredicto = 'terminada' | 'abandonada'

export interface Finalizada {
  /** Version del formato, por si algun dia hay que migrar lo guardado. */
  v: number
  estado: Veredicto
  /** Cuando se pulso el boton, en milisegundos. */
  cuando: number
  /** Primera lectura conocida, o 0 si la fuente nunca guardo fechas. */
  inicio: number
  /** Ultima lectura conocida, o 0. Es el "cuando la terminaste" de verdad. */
  fin: number
  leidos: number
  total: number
  /** De 0 (sin puntuar) a 5. */
  estrellas: number
  /** Lo que te parecio, en tus palabras. Vacio si no escribiste nada. */
  nota: string
}

export const VERSION = 1

export function leerFinalizada(meta: EntradaMeta[] | undefined): Finalizada | null {
  const entrada = meta?.find((m) => m.key === CLAVE_META)
  if (!entrada) return null
  try {
    const crudo = JSON.parse(entrada.value) as Partial<Finalizada>
    return {
      v: Number(crudo.v) || VERSION,
      estado: crudo.estado === 'abandonada' ? 'abandonada' : 'terminada',
      cuando: Number(crudo.cuando) || 0,
      inicio: Number(crudo.inicio) || 0,
      fin: Number(crudo.fin) || 0,
      leidos: Number(crudo.leidos) || 0,
      total: Number(crudo.total) || 0,
      estrellas: Math.min(5, Math.max(0, Number(crudo.estrellas) || 0)),
      nota: typeof crudo.nota === 'string' ? crudo.nota : '',
    }
  } catch {
    return null
  }
}

/**
 * Saca de los capitulos lo que hace falta para el estante.
 *
 * Las fechas salen de `lastReadAt`, que es lo unico que sabe cuando leiste
 * cada capitulo. **No siempre esta**: en One Piece no lo tiene ni uno de los
 * doce abiertos, asi que los ceros son un caso normal y no un fallo; cuando
 * faltan, la fecha que se ensena es la del dia que marcaste la serie.
 *
 * Si la serie ya estaba marcada se conservan la puntuacion y la nota: volver
 * a marcarla —por ejemplo para cambiar de "abandonada" a "terminada"— no
 * deberia borrar lo que escribiste.
 */
export function resumirLectura(
  capitulos: Capitulo[],
  estado: Veredicto,
  previa?: Finalizada | null,
): Finalizada {
  const fechas = capitulos
    .filter((c) => c.isRead || c.lastPageRead > 0)
    .map((c) => aMilisegundos(c.lastReadAt))
    .filter((ms) => ms > 0)

  return {
    v: VERSION,
    estado,
    cuando: Date.now(),
    inicio: fechas.length ? Math.min(...fechas) : 0,
    fin: fechas.length ? Math.max(...fechas) : 0,
    leidos: capitulos.filter((c) => c.isRead).length,
    total: capitulos.length,
    estrellas: previa?.estrellas ?? 0,
    nota: previa?.nota ?? '',
  }
}

/** La fecha que se ensena: cuando la dejaste de verdad, o cuando la marcaste. */
export function fechaDeFinal(finalizada: Finalizada): number {
  return finalizada.fin || finalizada.cuando
}

/**
 * Cuanto te duro, de la primera lectura a la ultima.
 *
 * Es aproximado a proposito: `lastReadAt` se pisa al releer un capitulo, asi
 * que lo que mide es "cuanto tiempo estuviste con ella", no una cronologia
 * exacta. Por debajo de dos dias no se dice nada, que ahi el dato no aporta y
 * ademas suele ser una serie que se leyo del tiron.
 */
export function comoDuracion(finalizada: Finalizada): string {
  const { inicio, fin } = finalizada
  if (!inicio || !fin) return ''
  const dias = Math.round((fin - inicio) / 86400000)
  if (dias < 2) return ''
  if (dias < 45) return `${dias} dias`
  const meses = Math.round(dias / 30.44)
  if (meses < 24) return `${meses} meses`
  const anos = Math.floor(meses / 12)
  const resto = meses % 12
  const parte = `${anos} ${anos === 1 ? 'ano' : 'anos'}`
  return resto ? `${parte} y ${resto} ${resto === 1 ? 'mes' : 'meses'}` : parte
}

/**
 * Cambia una clave de la lista de metadatos sin tocar las demas.
 *
 * Sirve para corregir la respuesta ya cacheada de la biblioteca al puntuar o
 * escribir una nota, y que la estrella se pinte al tocarla en vez de esperar
 * al viaje de ida y vuelta al VPS.
 */
export function conMeta(meta: EntradaMeta[], clave: string, valor: string | null): EntradaMeta[] {
  const resto = meta.filter((m) => m.key !== clave)
  return valor === null ? resto : [...resto, { key: clave, value: valor }]
}
